// Barsys Analytics › BigQuery Insights: barsys.com and the landing sites
// (360 / ShakerPro / Coaster / us.) from the GA4 BigQuery export. Adds what
// the GA4 Data API pages do not show: per-site sales, the checkout funnel,
// source/campaign/landing-page conversion and customer signals.

import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { COLORS, DataTable, DaysPicker, Section, StateBanner, Tile, chartTheme, fmtInt, fmtMoney, fmtPct, useBqInsights } from "../../components/bigquery/bqShared.jsx";

export default function WebsiteBigQuery() {
  const [days, setDays] = useState(30);
  // "all" = the existing all-sites funnel; otherwise a host from data.hosts, read from
  // data.funnel_by_site (the API returns every host's funnel, so no extra request).
  const [funnelSite, setFunnelSite] = useState("all");
  const { data, loading, error } = useBqInsights("website", days);
  const t = chartTheme();

  const trendChart = useMemo(() => {
    const rows = data?.trend || [];
    return {
      series: [
        { name: "Sessions", type: "line", data: rows.map((r) => r.sessions) },
        { name: "Add-to-cart sessions", type: "line", data: rows.map((r) => r.carts) },
        { name: "Purchases", type: "column", data: rows.map((r) => r.purchases) },
      ],
      options: {
        chart: { ...t.chart },
        colors: [COLORS[1], COLORS[2], COLORS[0]],
        stroke: { width: [2, 2, 0], curve: "smooth" },
        xaxis: { categories: rows.map((r) => r.date), labels: { ...t.xaxisLabels, rotate: -45 }, tickAmount: Math.min(rows.length, 12) },
        yaxis: [{ labels: t.yaxisLabels, title: { text: "Sessions" } }, { show: false }, { opposite: true, labels: t.yaxisLabels, title: { text: "Purchases" } }],
        legend: t.legend, grid: t.grid, tooltip: t.tooltip, dataLabels: t.dataLabels,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const funnelAll = funnelSite === "all" ? data?.funnel : data?.funnel_by_site?.[funnelSite];
  const funnelSessions = funnelAll?.[0]?.sessions;
  // Sessions but no ecommerce step at all: the site is not sending those GA4 events.
  const funnelNoSteps = funnelSessions > 0 && (funnelAll || []).slice(1).every((r) => !r.sessions);

  const funnelChart = useMemo(() => {
    const rows = (funnelAll || []).slice(1);
    return {
      series: [{ name: "Sessions", data: rows.map((r) => r.sessions) }],
      options: {
        chart: { ...t.chart, type: "bar" },
        colors: [COLORS[0]],
        plotOptions: { bar: { horizontal: true, barHeight: "65%", distributed: false } },
        dataLabels: { enabled: true, formatter: (v, o) => `${fmtInt(v)} · ${fmtPct(rows[o.dataPointIndex]?.pct_of_sessions)}`, style: { fontSize: "11px" } },
        xaxis: { categories: rows.map((r) => r.label), labels: t.xaxisLabels },
        yaxis: { labels: t.yaxisLabels },
        grid: t.grid, tooltip: t.tooltip, legend: { show: false },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, funnelSite]);

  const totals = useMemo(() => {
    const s = data?.sites || [];
    const sum = (k) => s.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    return { sessions: sum("sessions"), users: sum("users"), orders: sum("orders"), revenue: sum("revenue"), carts: sum("cart_sessions") };
  }, [data]);
  const c = data?.customers || {};

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">BARSYS ANALYTICS</li>
            <li className="breadcrumb-item active">BIGQUERY INSIGHTS</li>
          </ul>
          <h1 className="page-header mb-0">
            Website <small>barsys.com + landing sites, from BigQuery</small>
          </h1>
          <div className="small text-inverse text-opacity-50 mt-1">Source: GA4 BigQuery export (property 454856112) · sites: {(data?.hosts || []).join(", ")}</div>
        </div>
        <div className="ms-auto mt-2 mt-md-0">
          <DaysPicker days={days} setDays={setDays} disabled={loading} />
        </div>
      </div>
      <StateBanner loading={loading} error={error} hasData={!!data} />

      {data && (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          <div className="row">
            <Tile label="Sessions" value={fmtInt(totals.sessions)} />
            <Tile label="Visitors" value={fmtInt(totals.users)} />
            <Tile label="Add-to-cart sessions" value={fmtInt(totals.carts)} sub={fmtPct(totals.sessions ? Math.round((totals.carts / totals.sessions) * 1000) / 10 : null) + " of sessions"} />
            <Tile label="Orders (GA-tracked)" value={fmtInt(totals.orders)} sub={fmtPct(totals.sessions ? Math.round((totals.orders / totals.sessions) * 10000) / 100 : null) + " conversion"} />
            <Tile label="Revenue (GA-tracked)" value={fmtMoney(totals.revenue)} sub={totals.orders ? `${fmtMoney(totals.revenue / totals.orders)} per order` : null} />
            <Tile label="Cart abandoners (all time)" value={fmtInt(c.cart_abandoners)} sub={`${fmtInt(c.checkout_abandoners)} abandoned at checkout`} />
          </div>

          <Section title="BY SITE" subtitle={`last ${data.window_days} days`} bodyClass="p-0">
            <DataTable
              rows={data.sites}
              columns={[
                { key: "site", label: "Site" },
                { key: "sessions", label: "Sessions", num: true },
                { key: "users", label: "Visitors", num: true },
                { key: "page_views", label: "Page views", num: true },
                { key: "engaged_pct", label: "Engaged", num: true, fmt: fmtPct },
                { key: "cart_sessions", label: "Cart sessions", num: true },
                { key: "cart_pct", label: "Cart rate", num: true, fmt: fmtPct },
                { key: "orders", label: "Orders", num: true },
                { key: "conversion_pct", label: "Conversion", num: true, fmt: fmtPct },
                { key: "revenue", label: "Revenue", num: true, fmt: fmtMoney },
              ]}
            />
          </Section>

          <div className="row">
            <div className="col-xl-7">
              <Section title="DAILY TREND">
                <Chart type="line" height={300} series={trendChart.series} options={trendChart.options} />
              </Section>
            </div>
            <div className="col-xl-5">
              <Section
                title="CHECKOUT FUNNEL"
                subtitle={`${fmtInt(funnelSessions)} sessions`}
                right={
                  // Only offered once the API sends per-site funnels, so the page is safe to ship first.
                  data.funnel_by_site && (
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto", minWidth: 170 }}
                    value={funnelSite}
                    onChange={(e) => setFunnelSite(e.target.value)}
                    aria-label="Filter checkout funnel by site"
                  >
                    <option value="all">All sites</option>
                    {(data.hosts || []).map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  )
                }
              >
                {funnelNoSteps ? (
                  <div className="text-center text-inverse text-opacity-50 py-5">
                    {funnelSite} had sessions but no checkout events (product view, cart, checkout, purchase) were recorded in GA4 for this window.
                  </div>
                ) : funnelSessions === 0 ? (
                  <div className="text-center text-inverse text-opacity-50 py-5">{funnelSite === "all" ? "No sessions in this window." : `No sessions on ${funnelSite} in this window.`}</div>
                ) : (
                  <Chart type="bar" height={300} series={funnelChart.series} options={funnelChart.options} />
                )}
                <div className="small text-inverse text-opacity-50">{data.notes?.funnel}</div>
              </Section>
            </div>
          </div>

          <div className="row">
            <div className="col-xl-7">
              <Section title="TRAFFIC SOURCES" subtitle="source / medium" bodyClass="p-0">
                <DataTable
                  rows={data.traffic_sources}
                  columns={[
                    { key: "source", label: "Source / medium", render: (r) => `${r.source} / ${r.medium}` },
                    { key: "sessions", label: "Sessions", num: true },
                    { key: "users", label: "Visitors", num: true },
                    { key: "engaged_pct", label: "Engaged", num: true, fmt: fmtPct },
                    { key: "carts", label: "Carts", num: true },
                    { key: "purchases", label: "Purchases", num: true },
                    { key: "conversion_pct", label: "Conv.", num: true, fmt: fmtPct },
                    { key: "revenue", label: "Revenue", num: true, fmt: fmtMoney },
                  ]}
                />
              </Section>
            </div>
            <div className="col-xl-5">
              <Section title="CAMPAIGNS" bodyClass="p-0">
                <DataTable
                  rows={data.campaigns}
                  columns={[
                    { key: "campaign", label: "Campaign", width: 260 },
                    { key: "sessions", label: "Sessions", num: true },
                    { key: "purchases", label: "Purchases", num: true },
                    { key: "conversion_pct", label: "Conv.", num: true, fmt: fmtPct },
                    { key: "revenue", label: "Revenue", num: true, fmt: fmtMoney },
                  ]}
                />
              </Section>
            </div>
          </div>

          <Section title="LANDING PAGES" subtitle="first page of the session" bodyClass="p-0">
            <DataTable
              rows={data.landing_pages}
              columns={[
                { key: "landing_page", label: "Page", width: 380 },
                { key: "title", label: "Title", width: 300 },
                { key: "sessions", label: "Sessions", num: true },
                { key: "purchases", label: "Purchases", num: true },
                { key: "conversion_pct", label: "Conv.", num: true, fmt: fmtPct },
                { key: "revenue", label: "Revenue", num: true, fmt: fmtMoney },
              ]}
            />
          </Section>

          <div className="row">
            <div className="col-md-4">
              <Section title="DEVICES" bodyClass="p-0">
                <DataTable
                  rows={data.devices}
                  columns={[
                    { key: "device", label: "Device" },
                    { key: "sessions", label: "Sessions", num: true },
                    { key: "purchases", label: "Purchases", num: true },
                    { key: "revenue", label: "Revenue", num: true, fmt: fmtMoney },
                  ]}
                />
              </Section>
            </div>
            <div className="col-md-4">
              <Section title="COUNTRIES" bodyClass="p-0">
                <DataTable
                  rows={data.countries}
                  columns={[
                    { key: "country", label: "Country" },
                    { key: "sessions", label: "Sessions", num: true },
                    { key: "users", label: "Visitors", num: true },
                    { key: "purchases", label: "Purchases", num: true },
                  ]}
                />
              </Section>
            </div>
            <div className="col-md-4">
              <Section title="CUSTOMER SIGNALS" subtitle="all visitors since export start" bodyClass="p-0">
                <DataTable
                  rows={[
                    { k: "Visitors", v: fmtInt(c.visitors) },
                    { k: `Active in last ${data.window_days} days`, v: fmtInt(c.active_in_window) },
                    { k: "Purchased", v: fmtInt(c.purchasers) },
                    { k: "Purchased 2+ times", v: fmtInt(c.repeat_purchasers) },
                    { k: "Added to cart, never bought", v: fmtInt(c.cart_abandoners) },
                    { k: "Reached checkout, never bought", v: fmtInt(c.checkout_abandoners) },
                    { k: "Browsed, never added to cart", v: fmtInt(c.browsers_no_cart) },
                    { k: "Engaged with Barbot", v: fmtInt(c.barbot_engaged) },
                    { k: "Engaged with ShakerPro", v: fmtInt(c.shakerpro_engaged) },
                    { k: "Lifetime revenue (GA)", v: fmtMoney(c.lifetime_revenue) },
                  ]}
                  columns={[{ key: "k", label: "Signal" }, { key: "v", label: "Visitors", num: true, render: (r) => r.v }]}
                />
              </Section>
            </div>
          </div>
          <div className="small text-inverse text-opacity-50 mb-4">{data.notes?.revenue}</div>
        </div>
      )}
    </div>
  );
}
